<template>
  <main class="public-booking">
    <section v-if="isSuccess" class="booking-shell">
      <div class="booking-panel booking-panel--center">
        <div class="brand-mark">Fusco Fit</div>
        <p class="status-label">Prenotazione confermata</p>
        <h1>Slot riservato</h1>
        <p class="lead">
          Abbiamo registrato la tua prenotazione per
          <strong>{{ successDateLabel }}</strong> alle <strong>{{ successTime }}</strong>.
        </p>
        <div class="booking-recap">
          <div>
            <span>ID prenotazione</span>
            <strong>{{ $route.query.bookingId || '-' }}</strong>
          </div>
          <div>
            <span>Servizio</span>
            <strong>{{ serviceName }}</strong>
          </div>
          <div>
            <span>Stato</span>
            <strong>Confermata</strong>
          </div>
        </div>
        <div class="booking-actions">
          <router-link class="btn primary" to="/user">Vai all'area utente</router-link>
          <a class="btn ghost" :href="landingUrl">Torna a Fusco Fit</a>
        </div>
      </div>
    </section>

    <section v-else class="booking-grid">
      <aside class="booking-summary">
        <a class="back-link" :href="landingUrl">Torna a Fusco Fit</a>
        <div class="booking-card">
          <span class="eyebrow">Prenotazione</span>
          <h1>{{ serviceName }}</h1>
          <p>{{ serviceDescription }}</p>
          <dl>
            <div>
              <dt>Durata slot</dt>
              <dd>{{ slotDurationLabel }}</dd>
            </div>
            <div>
              <dt>Luogo</dt>
              <dd>{{ service?.location || 'Video call' }}</dd>
            </div>
            <div>
              <dt>Fuso orario</dt>
              <dd>{{ service?.timezone || 'Europe/Rome' }}</dd>
            </div>
          </dl>
        </div>
      </aside>

      <div class="booking-flow">
        <section class="booking-panel">
          <div class="panel-title">
            <span>1</span>
            <div>
              <h2>Seleziona il giorno</h2>
              <p>Vedi tutte le date disponibili e scegli quella che preferisci.</p>
            </div>
          </div>

          <div v-if="availabilityLoading" class="booking-muted">Caricamento disponibilita...</div>
          <p v-else-if="availabilityError" class="message error">{{ availabilityError }}</p>

          <div v-else class="calendar-layout">
            <div v-if="visibleDates.length" class="date-list" aria-label="Date disponibili">
              <button
                v-for="date in visibleDates"
                :key="date.dateKey"
                type="button"
                :class="['date-button', selectedDateKey === date.dateKey ? 'active' : '', !date.available ? 'disabled' : '']"
                :disabled="!date.available"
                @click="selectDate(date.dateKey)"
              >
                <span>{{ formatWeekday(date.dateKey) }}</span>
                <strong>{{ formatDayMonth(date.dateKey) }}</strong>
                <small>{{ availableSlotsCount(date) }} slot</small>
              </button>
            </div>
            <p v-else class="booking-muted">Al momento non ci sono date prenotabili.</p>
          </div>
        </section>

        <section v-if="selectedDate" ref="slotStep" class="booking-panel">
          <div class="panel-title">
            <span>2</span>
            <div>
              <h2>Seleziona orario</h2>
              <p>{{ selectedDateLabel }}</p>
            </div>
          </div>

          <div v-if="selectedDateSlots.length" class="slot-grid">
            <button
              v-for="slot in selectedDateSlots"
              :key="slot.id"
              type="button"
              :class="['slot-button', selectedSlot?.id === slot.id ? 'active' : '']"
              :disabled="!slot.available"
              @click="selectSlot(slot)"
            >
              <strong>{{ slot.timeKey }}</strong>
              <span>{{ slot.available ? 'Disponibile' : 'Occupato' }}</span>
            </button>
          </div>
          <p v-else class="booking-muted">Nessuno slot disponibile per questa data.</p>
        </section>

        <section v-if="selectedSlot" ref="authStep" class="booking-panel">
          <div class="panel-title">
            <span>3</span>
            <div>
              <h2>Accedi o registrati</h2>
              <p>Usiamo lo stesso account Fusco Fit del checkout.</p>
            </div>
          </div>

          <div v-if="user" class="signed-box">
            <div>
              <strong>{{ profile.email }}</strong>
              <span>Account collegato</span>
            </div>
            <button type="button" class="link-button" @click="logout">Cambia account</button>
          </div>

          <div v-else>
            <div class="auth-tabs" role="tablist" aria-label="Accesso prenotazione">
              <button type="button" :class="{ active: authMode === 'login' }" @click="authMode = 'login'">Accedi</button>
              <button type="button" :class="{ active: authMode === 'register' }" @click="authMode = 'register'">Registrati</button>
            </div>

            <form v-if="authMode === 'login'" class="form-grid" @submit.prevent="login">
              <label>
                Email
                <input v-model.trim="loginForm.email" type="email" autocomplete="email" required />
              </label>
              <label>
                Password
                <input v-model="loginForm.password" type="password" autocomplete="current-password" required />
              </label>
              <div class="form-actions">
                <button class="btn primary" type="submit" :disabled="authLoading">
                  {{ authLoading ? 'Accesso...' : 'Accedi' }}
                </button>
                <button class="link-button" type="button" :disabled="authLoading" @click="resetPassword">
                  Password dimenticata
                </button>
              </div>
            </form>

            <form v-else class="form-grid" @submit.prevent="register">
              <div class="two-cols">
                <label>
                  Nome
                  <input v-model.trim="registerForm.firstName" autocomplete="given-name" required />
                </label>
                <label>
                  Cognome
                  <input v-model.trim="registerForm.lastName" autocomplete="family-name" required />
                </label>
              </div>
              <label>
                Email
                <input v-model.trim="registerForm.email" type="email" autocomplete="email" required />
              </label>
              <label>
                Telefono
                <input v-model.trim="registerForm.phone" autocomplete="tel" required />
              </label>
              <label>
                Password
                <input v-model="registerForm.password" type="password" autocomplete="new-password" minlength="6" required />
              </label>
              <button class="btn primary" type="submit" :disabled="authLoading">
                {{ authLoading ? 'Creazione...' : 'Crea account' }}
              </button>
            </form>
          </div>

          <p v-if="authMessage" class="message">{{ authMessage }}</p>
          <p v-if="authError" class="message error">{{ authError }}</p>
        </section>

        <section v-if="selectedSlot && user" ref="confirmStep" class="booking-panel">
          <div class="panel-title">
            <span>4</span>
            <div>
              <h2>Conferma</h2>
              <p>Controlla i dati e conferma la prenotazione.</p>
            </div>
          </div>

          <form class="form-grid" @submit.prevent>
            <div class="two-cols">
              <label>
                Nome
                <input v-model.trim="profile.firstName" :disabled="!user" required />
              </label>
              <label>
                Cognome
                <input v-model.trim="profile.lastName" :disabled="!user" required />
              </label>
            </div>
            <label>
              Email
              <input v-model.trim="profile.email" type="email" disabled />
            </label>
            <label>
              Telefono
              <input v-model.trim="profile.phone" :disabled="!user" required />
            </label>
          </form>

          <div v-if="selectedSlot" class="selected-slot">
            <span>Slot selezionato</span>
            <strong>{{ selectedDateLabel }} - {{ selectedSlot.timeKey }}</strong>
          </div>

          <label class="terms-row">
            <input v-model="acceptedPrivacy" type="checkbox" :disabled="!user || bookingLoading" />
            <span>
              Accetto il trattamento dei dati per la gestione della prenotazione e dichiaro di aver letto
              <a :href="legalUrl('privacy-policy')" target="_blank" rel="noopener">privacy policy</a>,
              <a :href="legalUrl('termini-e-condizioni')" target="_blank" rel="noopener">termini e condizioni</a>
              e
              <a :href="legalUrl('scheda-servizi')" target="_blank" rel="noopener">scheda servizi</a>.
            </span>
          </label>

          <button class="btn primary full" type="button" :disabled="!canConfirmBooking" @click="confirmBooking">
            {{ bookingLoading ? 'Confermo...' : 'Conferma prenotazione' }}
          </button>
          <p v-if="bookingError" class="message error">{{ bookingError }}</p>
        </section>
      </div>
    </section>
  </main>
</template>

<script>
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
} from 'firebase/auth';
import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { auth, db } from '@/firebaseConfig';
import {
  getCreatePublicBookingFunctionUrl,
  getPublicBookingAvailabilityFunctionUrl,
} from '@/lib/env';

const CONSULTATION_DESCRIPTION = 'Prenota una consulenza direttamente con Giampaolo Fusco, ovunque ti trovi: una video call interattiva per capire insieme quali saranno gli step e come iniziare il tuo percorso.';

export default {
  name: 'PublicBooking',
  data() {
    return {
      user: null,
      unsubscribeAuth: null,
      authMode: 'login',
      authLoading: false,
      authError: '',
      authMessage: '',
      availabilityLoading: false,
      availabilityError: '',
      bookingLoading: false,
      bookingError: '',
      service: null,
      dates: [],
      selectedDateKey: '',
      selectedSlot: null,
      acceptedPrivacy: false,
      loginForm: {
        email: '',
        password: '',
      },
      registerForm: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        password: '',
      },
      profile: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
      },
    };
  },
  computed: {
    serviceId() {
      return String(this.$route.query.service || 'consulenza').trim().toLowerCase();
    },
    source() {
      return String(this.$route.query.source || 'fuscofit').trim();
    },
    isSuccess() {
      return this.$route.path === '/prenota/success';
    },
    serviceName() {
      return this.service?.name || 'Consulenza Fusco Fit';
    },
    serviceDescription() {
      if (this.serviceId === 'consulenza') return CONSULTATION_DESCRIPTION;
      return this.service?.description || 'Scegli uno slot disponibile e conferma la prenotazione.';
    },
    visibleDates() {
      return this.dates || [];
    },
    selectedDate() {
      return this.visibleDates.find((date) => date.dateKey === this.selectedDateKey) || null;
    },
    selectedDateSlots() {
      return this.selectedDate?.slots || [];
    },
    slotDurationLabel() {
      const validMinutes = (value) => {
        const minutes = Number(value);
        return Number.isInteger(minutes) && minutes > 0 ? minutes : null;
      };
      const selectedMinutes = validMinutes(this.selectedSlot?.slotMinutes);
      if (selectedMinutes) return `${selectedMinutes} minuti`;

      const slots = this.selectedDate
        ? this.selectedDateSlots
        : this.visibleDates.flatMap((date) => date.slots || []);
      const durations = [...new Set(slots.map((slot) => validMinutes(slot.slotMinutes)).filter(Boolean))]
        .sort((first, second) => first - second);
      if (!durations.length) {
        const defaultMinutes = validMinutes(this.service?.slotMinutes);
        return defaultMinutes ? `${defaultMinutes} minuti` : 'Da definire';
      }
      return durations.length === 1
        ? `${durations[0]} minuti`
        : `${durations[0]}–${durations[durations.length - 1]} minuti`;
    },
    selectedDateLabel() {
      if (!this.selectedDateKey) return 'Seleziona una data';
      return this.formatFullDate(this.selectedDateKey);
    },
    successDateLabel() {
      return this.$route.query.date ? this.formatFullDate(String(this.$route.query.date)) : '-';
    },
    successTime() {
      return String(this.$route.query.time || '-');
    },
    canConfirmBooking() {
      return Boolean(
        this.user &&
        this.selectedSlot &&
        this.profile.firstName &&
        this.profile.lastName &&
        this.profile.email &&
        this.profile.phone &&
        this.acceptedPrivacy &&
        !this.bookingLoading
      );
    },
    publicSiteBaseUrl() {
      if (typeof window === 'undefined') return 'https://www.fusco.fit';
      const { hostname, pathname } = window.location;
      if (hostname === 'www.valeriobianconi.it' && pathname.startsWith('/fuscofit')) {
        return 'https://www.valeriobianconi.it/fuscofit';
      }
      return 'https://www.fusco.fit';
    },
    landingUrl() {
      return `${this.publicSiteBaseUrl}/`;
    },
  },
  mounted() {
    this.loadAvailability();
    this.unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      this.user = user || null;
      this.authError = '';
      if (user) await this.loadUserProfile(user);
      if (user && this.selectedSlot) this.scrollToStep('confirmStep');
    });
  },
  beforeUnmount() {
    if (this.unsubscribeAuth) this.unsubscribeAuth();
  },
  methods: {
    legalUrl(slug) {
      return `${this.publicSiteBaseUrl}/${slug}/`;
    },
    async loadAvailability() {
      this.availabilityLoading = true;
      this.availabilityError = '';
      try {
        const url = new URL(getPublicBookingAvailabilityFunctionUrl());
        url.searchParams.set('service', this.serviceId);
        const resp = await fetch(url.toString());
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(payload?.error || 'Non riesco a leggere le disponibilita.');
        this.service = payload.service || null;
        this.dates = Array.isArray(payload.dates) ? payload.dates : [];
        this.selectedDateKey = '';
        this.selectedSlot = null;
      } catch (err) {
        console.error('Public booking availability error:', err);
        this.availabilityError = err?.message || 'Errore nel caricamento delle disponibilita.';
      } finally {
        this.availabilityLoading = false;
      }
    },
    selectDate(dateKey) {
      this.selectedDateKey = dateKey;
      this.selectedSlot = null;
      this.acceptedPrivacy = false;
      this.bookingError = '';
      this.scrollToStep('slotStep');
    },
    selectSlot(slot) {
      if (!slot?.available) return;
      this.selectedSlot = slot;
      this.acceptedPrivacy = false;
      this.bookingError = '';
      this.scrollToStep(this.user ? 'confirmStep' : 'authStep');
    },
    scrollToStep(refName) {
      this.$nextTick(() => {
        window.requestAnimationFrame(() => {
          const target = this.$refs[refName];
          const element = Array.isArray(target) ? target[0] : target;
          if (!element || !element.scrollIntoView) return;
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      });
    },
    availableSlotsCount(date) {
      return (date.slots || []).filter((slot) => slot.available).length;
    },
    dateFromKey(dateKey) {
      return new Date(`${dateKey}T12:00:00`);
    },
    formatWeekday(dateKey) {
      return this.dateFromKey(dateKey).toLocaleDateString('it-IT', { weekday: 'short' });
    },
    formatDayMonth(dateKey) {
      return this.dateFromKey(dateKey).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
    },
    formatFullDate(dateKey) {
      return this.dateFromKey(dateKey).toLocaleDateString('it-IT', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      });
    },
    splitDisplayName(name = '') {
      const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
      return {
        firstName: parts.shift() || '',
        lastName: parts.join(' ') || '',
      };
    },
    async loadUserProfile(user) {
      const fallback = this.splitDisplayName(user.displayName || '');
      const nextProfile = {
        firstName: fallback.firstName,
        lastName: fallback.lastName,
        email: user.email || '',
        phone: '',
      };

      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (snap.exists()) {
          const data = snap.data() || {};
          nextProfile.firstName = data.name || data.firstName || nextProfile.firstName;
          nextProfile.lastName = data.surname || data.lastName || nextProfile.lastName;
          nextProfile.email = data.email || user.email || nextProfile.email;
          nextProfile.phone = data.phone || data.mobile || nextProfile.phone;
        }
      } catch (err) {
        console.warn('Errore recupero profilo booking:', err);
      }

      this.profile = nextProfile;
      if (!this.loginForm.email) this.loginForm.email = nextProfile.email;
    },
    async login() {
      this.authLoading = true;
      this.authError = '';
      this.authMessage = '';
      try {
        await signInWithEmailAndPassword(auth, this.loginForm.email.trim(), this.loginForm.password);
      } catch (err) {
        console.error('Public booking login error:', err);
        this.authError = 'Email o password non corretti.';
      } finally {
        this.authLoading = false;
      }
    },
    async register() {
      this.authLoading = true;
      this.authError = '';
      this.authMessage = '';
      try {
        const firstName = this.registerForm.firstName.trim();
        const lastName = this.registerForm.lastName.trim();
        const email = this.registerForm.email.trim().toLowerCase();
        const phone = this.registerForm.phone.trim();
        if (!firstName || !lastName || !email || !phone) throw new Error('Compila tutti i dati richiesti.');

        const credential = await createUserWithEmailAndPassword(auth, email, this.registerForm.password);
        await updateProfile(credential.user, { displayName: `${firstName} ${lastName}`.trim() });
        await setDoc(doc(db, 'users', credential.user.uid), {
          name: firstName,
          surname: lastName,
          email,
          emailLower: email,
          phone,
          uid: credential.user.uid,
          created_time: serverTimestamp(),
          updatedAt: new Date().toISOString(),
        }, { merge: true });
        await this.loadUserProfile(credential.user);
      } catch (err) {
        console.error('Public booking register error:', err);
        if (err?.code === 'auth/email-already-in-use') {
          this.authError = 'Email gia registrata. Usa la scheda Accedi.';
          this.authMode = 'login';
          this.loginForm.email = this.registerForm.email;
        } else {
          this.authError = err?.message || 'Errore durante la registrazione.';
        }
      } finally {
        this.authLoading = false;
      }
    },
    async resetPassword() {
      this.authError = '';
      this.authMessage = '';
      const email = this.loginForm.email.trim();
      if (!email) {
        this.authError = 'Inserisci la tua email prima di richiedere il reset.';
        return;
      }
      try {
        await sendPasswordResetEmail(auth, email);
        this.authMessage = 'Email di recupero password inviata.';
      } catch (err) {
        console.error('Public booking reset error:', err);
        this.authError = 'Non sono riuscito a inviare il recupero password.';
      }
    },
    async logout() {
      await signOut(auth);
      this.profile = { firstName: '', lastName: '', email: '', phone: '' };
    },
    async saveProfile() {
      if (!this.user) throw new Error('Accesso richiesto.');
      const email = (this.user.email || this.profile.email || '').trim().toLowerCase();
      await setDoc(doc(db, 'users', this.user.uid), {
        name: this.profile.firstName.trim(),
        surname: this.profile.lastName.trim(),
        email,
        emailLower: email,
        phone: this.profile.phone.trim(),
        uid: this.user.uid,
        updatedAt: new Date().toISOString(),
      }, { merge: true });
      this.profile.email = email;
    },
    async confirmBooking() {
      if (!this.canConfirmBooking) return;
      this.bookingLoading = true;
      this.bookingError = '';
      try {
        await this.saveProfile();
        const token = await this.user.getIdToken();
        const resp = await fetch(getCreatePublicBookingFunctionUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            serviceId: this.service?.serviceId || this.serviceId,
            dateKey: this.selectedSlot.dateKey,
            timeKey: this.selectedSlot.timeKey,
            source: this.source,
            customer: {
              firstName: this.profile.firstName,
              lastName: this.profile.lastName,
              email: this.profile.email,
              phone: this.profile.phone,
            },
          }),
        });
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(payload?.error || 'Non riesco a confermare la prenotazione.');
        this.$router.push({
          path: '/prenota/success',
          query: {
            bookingId: payload.bookingId || '',
            service: payload.serviceId || this.serviceId,
            date: payload.dateKey || this.selectedSlot.dateKey,
            time: payload.timeKey || this.selectedSlot.timeKey,
          },
        });
      } catch (err) {
        console.error('Public booking create error:', err);
        this.bookingError = err?.message || 'Errore durante la prenotazione.';
        await this.loadAvailability();
      } finally {
        this.bookingLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.public-booking {
  --ff-bg: #f5f4f0;
  --ff-surface: rgba(255, 255, 255, 0.82);
  --ff-ink: #111110;
  --ff-muted: #5f5f5b;
  --ff-soft: #ececea;
  --ff-line: rgba(17, 17, 16, 0.1);
  --ff-line-strong: rgba(17, 17, 16, 0.18);
  --ff-red: #c8102e;
  --ff-red-dark: #9e0b23;
  --ff-red-soft: rgba(200, 16, 46, 0.08);
  --ff-green: #17804a;
  --ff-green-soft: rgba(23, 128, 74, 0.1);
  --ff-green-shadow: rgba(23, 128, 74, 0.16);
  min-height: 100vh;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.54), rgba(255, 255, 255, 0) 280px),
    var(--ff-bg);
  color: var(--ff-ink);
  font-family: "Montserrat", Avenir, Helvetica, Arial, sans-serif;
}

.booking-grid {
  display: grid;
  grid-template-columns: minmax(292px, 380px) minmax(0, 1fr);
  gap: 24px;
  width: min(1180px, calc(100% - 40px));
  margin: 0 auto;
  padding: 34px 0 44px;
}

.booking-summary {
  position: sticky;
  top: 24px;
  align-self: start;
}

.back-link {
  display: inline-flex;
  align-items: center;
  min-height: 34px;
  margin-bottom: 12px;
  color: var(--ff-muted);
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-decoration: none;
  text-transform: uppercase;
}

.back-link:hover,
.link-button:hover {
  color: var(--ff-red);
}

.booking-card,
.booking-panel {
  border: 1px solid var(--ff-line);
  border-radius: 8px;
  box-shadow: 0 18px 54px rgba(17, 17, 16, 0.08);
}

.booking-card {
  position: relative;
  overflow: hidden;
  padding: 28px;
  background: var(--ff-ink);
  color: #fff;
}

.booking-card::before {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.11), transparent 34%, rgba(200, 16, 46, 0.18));
}

.booking-card > * {
  position: relative;
  z-index: 1;
}

.booking-panel {
  background: var(--ff-surface);
  backdrop-filter: blur(18px) saturate(1.35);
  -webkit-backdrop-filter: blur(18px) saturate(1.35);
  scroll-margin-top: 22px;
}

.eyebrow,
.brand-mark,
.status-label {
  color: var(--ff-red);
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.booking-card h1,
.booking-panel h1 {
  margin: 12px 0;
  color: var(--ff-ink);
  font-size: clamp(1.8rem, 4vw, 2.6rem);
  font-weight: 900;
  line-height: 0.98;
  text-transform: uppercase;
}

.booking-card h1 {
  color: #fff;
}

.booking-card p,
.lead {
  color: var(--ff-muted);
  line-height: 1.65;
}

.booking-card p {
  color: rgba(255, 255, 255, 0.72);
}

.booking-card dl,
.booking-recap {
  display: grid;
  gap: 12px;
  margin-top: 22px;
}

.booking-card dl div,
.booking-recap div {
  border-top: 1px solid var(--ff-line);
  padding-top: 12px;
}

.booking-card dl div {
  border-top-color: rgba(255, 255, 255, 0.16);
}

dt,
.booking-recap span,
.selected-slot span {
  display: block;
  color: var(--ff-muted);
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.booking-card dt {
  color: rgba(255, 255, 255, 0.54);
}

dd,
.booking-recap strong,
.selected-slot strong {
  display: block;
  margin-top: 4px;
  color: var(--ff-ink);
  font-weight: 900;
  overflow-wrap: anywhere;
}

.booking-card dd {
  color: #fff;
}

.selected-slot {
  position: relative;
  overflow: hidden;
  margin-top: 18px;
  border: 1px solid rgba(23, 128, 74, 0.28);
  border-radius: 8px;
  background:
    linear-gradient(135deg, var(--ff-green-soft), rgba(255, 255, 255, 0.78) 58%),
    #fff;
  box-shadow: 0 16px 42px var(--ff-green-shadow);
  padding: 18px 18px 18px 22px;
}

.selected-slot::before {
  content: "";
  position: absolute;
  inset: 0 auto 0 0;
  width: 5px;
  background: var(--ff-green);
}

.selected-slot span {
  color: var(--ff-green);
  letter-spacing: 0.12em;
}

.selected-slot strong {
  margin-top: 8px;
  font-size: clamp(1.15rem, 2.4vw, 1.55rem);
  line-height: 1.2;
}

.booking-flow {
  display: grid;
  gap: 16px;
}

.booking-panel {
  padding: 24px;
}

.booking-panel--center {
  width: min(780px, calc(100% - 40px));
  margin: 44px auto;
  text-align: center;
}

.panel-title {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  margin-bottom: 20px;
}

.panel-title > span {
  display: grid;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  place-items: center;
  background: var(--ff-ink);
  color: #fff;
  font-weight: 900;
  flex: 0 0 auto;
}

.panel-title h2 {
  margin: 0;
  color: var(--ff-ink);
  font-size: 1.32rem;
  font-weight: 900;
  line-height: 1.15;
}

.panel-title p,
.booking-muted,
.message {
  margin: 6px 0 0;
  color: var(--ff-muted);
  line-height: 1.5;
}

.calendar-layout {
  display: grid;
  gap: 18px;
}

.date-list,
.slot-grid {
  display: grid;
  gap: 12px;
}

.date-list {
  grid-template-columns: repeat(auto-fit, minmax(132px, 1fr));
}

.slot-grid {
  grid-template-columns: repeat(auto-fit, minmax(118px, 1fr));
}

.date-button,
.slot-button,
.auth-tabs button,
.btn,
.link-button {
  font: inherit;
}

.date-button,
.slot-button {
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(17, 17, 16, 0.09);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.78);
  color: var(--ff-ink);
  cursor: pointer;
  text-align: left;
  box-shadow: 0 10px 28px rgba(17, 17, 16, 0.045);
  transition: border-color 0.2s, background 0.2s, transform 0.16s, box-shadow 0.2s, color 0.2s;
}

.date-button::before,
.slot-button::before {
  content: "";
  position: absolute;
  inset: 0 auto 0 0;
  width: 4px;
  background: transparent;
  transition: background 0.2s;
}

.date-button,
.slot-button {
  display: grid;
  gap: 6px;
  min-height: 88px;
  padding: 15px 16px;
}

.date-button {
  align-content: space-between;
}

.slot-button {
  min-height: 76px;
  align-content: center;
}

.date-button span,
.date-button small,
.slot-button span {
  color: var(--ff-muted);
  font-size: 0.68rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.date-button strong,
.slot-button strong {
  color: var(--ff-ink);
  font-size: 1.24rem;
  font-weight: 900;
  line-height: 1;
}

.slot-button strong {
  font-size: 1.32rem;
  font-variant-numeric: tabular-nums;
}

.date-button small,
.slot-button span {
  width: fit-content;
  border-radius: 999px;
  background: rgba(17, 17, 16, 0.055);
  padding: 4px 8px;
  line-height: 1;
}

.date-button.active,
.slot-button.active {
  border-color: rgba(23, 128, 74, 0.72);
  background:
    linear-gradient(135deg, var(--ff-green-soft), rgba(255, 255, 255, 0.84) 58%),
    #fff;
  box-shadow: 0 16px 42px var(--ff-green-shadow);
}

.date-button.active::before,
.slot-button.active::before {
  background: var(--ff-green);
}

.date-button.active small,
.slot-button.active span {
  background: var(--ff-green);
  color: #fff;
}

.date-button:not(.disabled):hover,
.slot-button:not(:disabled):hover {
  transform: translateY(-2px);
  border-color: rgba(200, 16, 46, 0.52);
  box-shadow: 0 16px 38px rgba(17, 17, 16, 0.08);
}

.date-button.disabled,
.slot-button:disabled {
  cursor: not-allowed;
  opacity: 0.42;
  box-shadow: none;
}

.slot-box h3 {
  margin: 0 0 12px;
  color: var(--ff-ink);
  font-size: 1.08rem;
  font-weight: 900;
}

.auth-tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  margin-bottom: 16px;
  padding: 4px;
  border: 1px solid var(--ff-line);
  border-radius: 999px;
  background: rgba(17, 17, 16, 0.04);
}

.auth-tabs button {
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--ff-muted);
  cursor: pointer;
  font-weight: 900;
  padding: 11px 14px;
}

.auth-tabs button.active {
  background: var(--ff-ink);
  color: #fff;
}

.form-grid {
  display: grid;
  gap: 14px;
}

.two-cols {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

label {
  display: grid;
  gap: 7px;
  color: var(--ff-ink);
  font-size: 0.82rem;
  font-weight: 900;
}

input {
  width: 100%;
  min-height: 46px;
  box-sizing: border-box;
  border: 1px solid var(--ff-line-strong);
  border-radius: 8px;
  background: #fff;
  color: var(--ff-ink);
  font: inherit;
  padding: 12px 13px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
}

input:focus {
  border-color: rgba(200, 16, 46, 0.58);
  box-shadow: 0 0 0 4px rgba(200, 16, 46, 0.1);
}

input:disabled {
  background: #eeeeea;
  color: #777774;
}

.form-actions,
.booking-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  border: 1px solid transparent;
  border-radius: 999px;
  cursor: pointer;
  font-weight: 900;
  padding: 0 20px;
  text-decoration: none;
  transition: transform 0.16s, background 0.2s, border-color 0.2s;
}

.btn:hover:not(:disabled) {
  transform: translateY(-1px);
}

.btn.primary {
  background: var(--ff-red);
  color: #fff;
}

.btn.primary:hover:not(:disabled) {
  background: var(--ff-red-dark);
}

.btn.ghost {
  background: #fff;
  color: var(--ff-ink);
  border-color: var(--ff-line-strong);
}

.btn.full {
  width: 100%;
  margin-top: 16px;
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.link-button {
  border: 0;
  background: transparent;
  color: var(--ff-red);
  cursor: pointer;
  font-weight: 900;
  padding: 0;
}

.signed-box {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  border: 1px solid rgba(200, 16, 46, 0.18);
  border-radius: 8px;
  background: var(--ff-red-soft);
  padding: 14px;
}

.signed-box strong,
.signed-box span {
  display: block;
}

.signed-box span {
  color: var(--ff-muted);
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.terms-row {
  display: flex;
  grid-template-columns: none;
  gap: 10px;
  align-items: flex-start;
  margin-top: 16px;
  color: var(--ff-muted);
  line-height: 1.45;
}

.terms-row input {
  width: auto;
  min-height: auto;
  margin-top: 2px;
  accent-color: var(--ff-red);
}

.terms-row a {
  color: var(--ff-red);
  font-weight: 900;
  text-decoration: none;
}

.terms-row a:hover {
  color: var(--ff-red-dark);
  text-decoration: underline;
  text-underline-offset: 3px;
}

.message.error {
  color: #b00020;
  font-weight: 900;
}

.muted {
  opacity: 0.58;
}

.booking-shell {
  min-height: 100vh;
  display: grid;
  align-items: center;
  padding: 34px 0;
}

@media (max-width: 920px) {
  .booking-grid {
    grid-template-columns: 1fr;
    padding: 18px 0 30px;
  }

  .booking-summary {
    position: static;
  }
}

@media (max-width: 560px) {
  .booking-grid,
  .booking-panel--center {
    width: min(100% - 24px, 780px);
  }

  .booking-panel--center {
    margin: 20px auto;
  }

  .two-cols {
    grid-template-columns: 1fr;
  }

  .booking-panel,
  .booking-card {
    padding: 18px;
  }

  .date-list,
  .slot-grid {
    gap: 10px;
  }

  .date-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .slot-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .date-button,
  .slot-button {
    min-height: 78px;
    padding: 13px;
  }

  .date-button strong,
  .slot-button strong {
    font-size: 1.08rem;
  }

  .slot-button strong {
    font-size: 1.16rem;
  }

  .signed-box,
  .form-actions,
  .booking-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .btn,
  .link-button {
    width: 100%;
  }
}

@media (max-width: 380px) {
  .date-list,
  .slot-grid {
    grid-template-columns: 1fr;
  }
}
</style>
